import React, { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { WIDGET_TYPES } from 'common/Widgets/constants';
import { DATA_RANGE_TYPE } from 'common/Widgets/Widget/constants';
import LoaderRibbed from 'components/lib/LoaderRibbed';
import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useSalesBattlecardContext } from 'components/SalesBattlecard/SalesBattlecardContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { useWidgetDataFetcher } from 'hooks/useWidgetDataFetcher';

import AISummary from './AISummary';
import AllReviews from './AllReviews';
import CommunitySwitcher from './CommunitySwitcher';
import RatingsSection from './RatingsSection';
import { useWidgetContext } from '../Widget/WidgetContext';

const Container = styled.div<{ isModalPreview?: boolean }>`
  display: flex;
  flex-flow: column;
  height: 100%;
  overflow: hidden;
  overflow: ${({ isModalPreview }) => (isModalPreview ? 'unset' : 'hidden')};
  width: ${({ isModalPreview }) => (isModalPreview ? '1000px' : 'unset')};
  margin-top: 25px;
  gap: 25px;
`;

const Row = styled.div<{ isModalPreview?: boolean }>`
  display: flex;
  flex-flow: row;
  flex: 1;
  overflow: ${({ isModalPreview }) => (isModalPreview ? 'unset' : 'auto')};
  gap: 20px;
`;

const LeftColumn = styled.div<{ isModalPreview?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: ${({ isModalPreview }) => (isModalPreview ? 'unset' : 'auto')};
`;

export const ReviewsSummaryWidgetContent = () => {
  const { communityId, viewFilters } = useCommunityViewContext();
  const { selectedCompetitorId: battlecardCompetitorId } = useSalesBattlecardContext();
  const [selectedCommunityId, setSelectedCommunityId] = useState<number>(communityId);
  const { filters, isModalPreview } = useWidgetContext();
  const { fetchWidgetData, snapshotUuids } = useWidgetDataFetcher();
  const { publication } = usePublicationContext();

  const handleCommunityChange = useCallback((newCommunityId: number) => {
    setSelectedCommunityId(newCommunityId);
  }, []);

  useEffect(() => {
    if (battlecardCompetitorId !== null) {
      setSelectedCommunityId(battlecardCompetitorId);
    } else if (communityId) {
      setSelectedCommunityId(communityId);
    }
  }, [battlecardCompetitorId, viewFilters?.competitors, communityId]);

  const { data: reviewsSummaryData, isFetching } = useQuery({
    queryKey: [
      'reviews-summary',
      communityId,
      selectedCommunityId,
      filters.reviewSources,
      filters.reviewCategories,
      snapshotUuids,
      publication?.hash,
    ],
    queryFn: () =>
      fetchWidgetData(communityId, WIDGET_TYPES.REVIEWS_SUMMARY, DATA_RANGE_TYPE.NOW, {
        filters: {
          competitors: selectedCommunityId !== communityId ? [selectedCommunityId] : [],
          care_types: [],
          room_types: [],
          care_levels: [],
          review_sources: filters.reviewSources,
          review_categories: filters.reviewCategories,
          swot_categories: [],
        },
        ...(publication?.hash ? { public_hash: publication.hash } : {}),
      }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
    enabled: communityId > 0 && snapshotUuids !== null,
  });

  const communityData = reviewsSummaryData?.[0];

  return (
    <Container isModalPreview={isModalPreview}>
      <CommunitySwitcher selectedCommunityId={selectedCommunityId} onCommunityChange={handleCommunityChange} />
      <Row isModalPreview={isModalPreview}>
        <LeftColumn isModalPreview={isModalPreview} className={isModalPreview ? '' : 'mini-scroll'}>
          {isFetching ? (
            <LoaderRibbed size={'small'} />
          ) : (
            <>
              <RatingsSection
                ratings={communityData?.ratings ?? null}
                comparisonTypeEnabled={filters?.comparisonTypeEnabled}
                valueDisplay={filters?.valueDisplay}
              />
              <AISummary aiInsights={communityData?.ai_insights ?? null} />
            </>
          )}
        </LeftColumn>
        <AllReviews selectedCommunityId={selectedCommunityId} />
      </Row>
    </Container>
  );
};

export default ReviewsSummaryWidgetContent;
